import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Plus, X, Undo2 } from 'lucide-react';
import Layout from '../../components/Layout';
import {
  purchaseOrderService,
  CreatePurchaseOrderLineRequest,
  CreatePurchaseOrderRequest,
} from '../../services/purchaseOrderService';
import { supplierService, SupplierDTO } from '../../services/supplierService';
import { purchaseItemService, PurchaseItemDTO } from '../../services/purchaseItemService';
import paymentTypeService, { PaymentType as PaymentTypeOption } from '../../services/paymentTypeService';
import { parseApiError } from '../../utils/errorUtils';
import './PurchaseOrderPage.css';

const NOTES_MAX_LENGTH = 500;

interface LineRow {
  purchaseItemId?: number;
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;   // % from purchase item — read-only
}

interface FormErrors {
  supplierId?: string;
  orderDate?: string;
  expectedDeliveryDate?: string;
  notes?: string;
  lines?: string;
}

interface LineError {
  quantity?: string;
  unitPrice?: string;
}

const emptyLine = (): LineRow => ({
  purchaseItemId: undefined,
  productId: 0,
  productName: '',
  quantity: 1,
  unitPrice: 0,
  taxRate: 0,
});

const PurchaseOrderCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [lineErrors, setLineErrors] = useState<Record<number, LineError>>({});
  const [suppliers, setSuppliers] = useState<SupplierDTO[]>([]);
  const [purchaseItems, setPurchaseItems] = useState<PurchaseItemDTO[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeOption[]>([]);
  const [paymentTypeId, setPaymentTypeId] = useState<number>(0);

  const today = new Date().toISOString().slice(0, 10);
  const [formData, setFormData] = useState({
    supplierId: '',
    orderDate: today,
    expectedDeliveryDate: '',
    notes: '',
  });
  const [lines, setLines] = useState<LineRow[]>([emptyLine()]);

  useEffect(() => {
    supplierService
      .getSuppliers({ pageNumber: 1, pageSize: 500 })
      .then((res) => {
        const data = res?.data?.data;
        if (Array.isArray(data)) setSuppliers(data);
      })
      .catch(() => {});

    paymentTypeService.getActivePaymentTypes('Purchase')
      .then((pts) => {
        const list: PaymentTypeOption[] = Array.isArray(pts) ? pts : [];
        setPaymentTypes(list);
        // Default to Credit payment type; leave unselected if none exists
        const credit = list.find(p => p.paymentCategory === 'Credit');
        if (credit) setPaymentTypeId(credit.id);
      })
      .catch(() => {});
  }, []);

  const handleSupplierChange = async (supplierId: string) => {
    setFormData((prev) => ({ ...prev, supplierId }));
    setFormErrors((prev) => ({ ...prev, supplierId: undefined }));
    setLines([emptyLine()]);
    setLineErrors({});
    setPurchaseItems([]);
    if (!supplierId) return;
    setLoadingItems(true);
    try {
      const res = await purchaseItemService.getPurchaseItemsBySupplier(Number(supplierId));
      const data = res?.data?.data ?? res?.data;
      if (Array.isArray(data)) setPurchaseItems(data);
    } catch {
      setPurchaseItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'supplierId') {
      handleSupplierChange(value);
      return;
    }

    let finalValue = value;
    if (name === 'notes' && value.length > NOTES_MAX_LENGTH) {
      finalValue = value.substring(0, NOTES_MAX_LENGTH);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleLineItemSelect = (index: number, purchaseItemId: string) => {
    const item = purchaseItems.find((p) => p.purchaseItemId === Number(purchaseItemId));
    setLines((prev) => {
      const updated = [...prev];
      if (item) {
        updated[index] = {
          purchaseItemId: item.purchaseItemId,
          productId: item.productId,
          productName: item.productName,
          quantity: updated[index].quantity || 1,
          unitPrice: item.purchasePrice,
          taxRate: item.taxPercentage ?? 0,
        };
      } else {
        updated[index] = emptyLine();
      }
      return updated;
    });
    setLineErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setFormErrors((prev) => ({ ...prev, lines: undefined }));
  };

  const handleLineChange = (
    index: number,
    field: 'quantity' | 'unitPrice',
    value: string
  ) => {
    setLines((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
      return updated;
    });
    setLineErrors((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: undefined },
    }));
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (index: number) => {
    if (lines.length > 1) {
      setLines((prev) => prev.filter((_, i) => i !== index));
      setLineErrors((prev) => {
        const next: Record<number, LineError> = {};
        Object.entries(prev).forEach(([key, val]) => {
          const k = Number(key);
          if (k < index) next[k] = val;
          else if (k > index) next[k - 1] = val;
        });
        return next;
      });
    }
  };

  const lineTaxAmt  = (l: LineRow) => l.quantity * l.unitPrice * (l.taxRate / 100);
  const lineTotal   = (l: LineRow) => l.quantity * l.unitPrice + lineTaxAmt(l);
  const subtotal    = lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0);
  const totalTax    = lines.reduce((s, l) => s + lineTaxAmt(l), 0);
  const grandTotal  = subtotal + totalTax;

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    const newLineErrors: Record<number, LineError> = {};
    let isValid = true;

    if (!formData.supplierId) {
      errors.supplierId = t('purchaseOrder.formValidation.supplierRequired');
      isValid = false;
    }

    if (!formData.orderDate) {
      errors.orderDate = t('purchaseOrder.formValidation.orderDateRequired');
      isValid = false;
    }

    if (!formData.expectedDeliveryDate) {
      errors.expectedDeliveryDate = t('purchaseOrder.formValidation.deliveryDateRequired');
      isValid = false;
    } else if (formData.orderDate && formData.expectedDeliveryDate < formData.orderDate) {
      errors.expectedDeliveryDate = t('purchaseOrder.formValidation.deliveryBeforeOrder');
      isValid = false;
    }

    if (formData.notes && formData.notes.length > NOTES_MAX_LENGTH) {
      errors.notes = t('purchaseOrder.formValidation.notesMaxLength');
      isValid = false;
    }

    const validLines = lines.filter((l) => l.productId > 0);
    if (validLines.length === 0) {
      errors.lines = t('purchaseOrder.formValidation.itemsRequired');
      isValid = false;
    } else {
      lines.forEach((l, index) => {
        if (l.productId > 0) {
          const lineErr: LineError = {};
          if (l.quantity <= 0) {
            lineErr.quantity = t('purchaseOrder.formValidation.quantityRequired');
            isValid = false;
          }
          if (l.unitPrice < 0) {
            lineErr.unitPrice = t('purchaseOrder.formValidation.unitPriceNegative');
            isValid = false;
          }
          if (Object.keys(lineErr).length > 0) {
            newLineErrors[index] = lineErr;
          }
        }
      });
    }

    setFormErrors(errors);
    setLineErrors(newLineErrors);

    if (!isValid) {
      const firstHeaderError = Object.values(errors).find((e) => e);
      const firstLineError = Object.values(newLineErrors)
        .flatMap((e) => Object.values(e))
        .find((e) => e);
      const firstError = firstHeaderError || firstLineError;
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setFormErrors({});
    setLineErrors({});
    if (!validateForm()) return;

    const lines_payload: CreatePurchaseOrderLineRequest[] = lines
      .filter((l) => l.productId > 0)
      .map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
      }));

    const payload: CreatePurchaseOrderRequest = {
      supplierId: Number(formData.supplierId),
      orderDate: formData.orderDate,
      expectedDeliveryDate: formData.expectedDeliveryDate || undefined,
      paymentTypeId: paymentTypeId > 0 ? paymentTypeId : undefined,
      notes: formData.notes || undefined,
      lines: lines_payload,
    };

    setSaving(true);
    try {
      await purchaseOrderService.createPurchaseOrder(payload);
      navigate('/purchase-orders', {
        state: { message: t('purchaseOrder.purchaseOrderCreatedSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('purchaseOrder.failedToCreate')).message);
      setSaving(false);
    }
  };

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  // ── Formatters
  const fmt = (n: number) =>
    new Intl.NumberFormat(langCode, { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  const validItemCount = lines.filter((l) => l.productId > 0).length;

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="po-create-page" noValidate>

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button
              type="button"
              className="ims-back-btn"
              onClick={() => navigate('/purchase-orders')}
            >
              <ArrowLeft size={14} strokeWidth={2} />
              {t('purchaseOrder.backToPurchaseOrders')}
            </button>
            <h1 className="ims-page-title">{t('purchaseOrder.createTitle')}</h1>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate('/purchase-orders')}
              disabled={saving}
            >
              <Undo2 size={15} strokeWidth={2} />
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? (
                <>
                  <span className="ims-btn-spinner" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Save size={15} strokeWidth={2} />
                  {t('purchaseOrder.createTitle')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="ims-banner-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          </div>
        )}

        {/* ── 3-column body ── */}
        <div className="ims-create-body">

          {/* LEFT — Order Info */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('purchaseOrder.orderInfo')}</h2>
              </div>
              <div className="ims-card-body">

                {/* Supplier */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('purchaseOrder.supplier')} <span className="required">*</span>
                  </label>
                  <select
                    className={`ims-form-control ${formErrors.supplierId ? 'is-invalid' : ''}`}
                    name="supplierId"
                    value={formData.supplierId}
                    onChange={handleFormChange}
                  >
                    <option value="">{t('purchaseOrder.selectSupplier')}</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.code})
                      </option>
                    ))}
                  </select>
                  {formErrors.supplierId && <span className="ims-field-error">{formErrors.supplierId}</span>}
                </div>

                {/* Dates */}
                <div className="ims-date-row">
                  <div className="ims-form-group">
                    <label className="ims-form-label">
                      {t('purchaseOrder.orderDate')} <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className={`ims-form-control ${formErrors.orderDate ? 'is-invalid' : ''}`}
                      name="orderDate"
                      value={formData.orderDate}
                      onChange={handleFormChange}
                    />
                    {formErrors.orderDate && <span className="ims-field-error">{formErrors.orderDate}</span>}
                  </div>
                  <div className="ims-form-group">
                    <label className="ims-form-label">
                      {t('purchaseOrder.expectedDelivery')} <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className={`ims-form-control ${formErrors.expectedDeliveryDate ? 'is-invalid' : ''}`}
                      name="expectedDeliveryDate"
                      value={formData.expectedDeliveryDate}
                      onChange={handleFormChange}
                    />
                    {formErrors.expectedDeliveryDate && <span className="ims-field-error">{formErrors.expectedDeliveryDate}</span>}
                  </div>
                </div>

                {/* Payment Type */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('purchaseOrder.paymentType', 'Payment Type')} <span className="required">*</span>
                  </label>
                  <select
                    className="ims-form-control"
                    value={paymentTypeId}
                    onChange={(e) => setPaymentTypeId(Number(e.target.value))}
                  >
                    <option value={0}>{t('purchaseOrder.selectPaymentType', 'Select payment type…')}</option>
                    {paymentTypes.map(pt => (
                      <option key={pt.id} value={pt.id}>{pt.name}</option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('purchaseOrder.notes')}</label>
                  <textarea
                    className={`ims-form-control ims-textarea ${formErrors.notes ? 'is-invalid' : ''}`}
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={3}
                    maxLength={NOTES_MAX_LENGTH}
                  />
                  {formErrors.notes && <span className="ims-field-error">{formErrors.notes}</span>}
                  {!formErrors.notes && (
                    <span className="ims-field-helper">
                      {formData.notes.length}/{NOTES_MAX_LENGTH}
                    </span>
                  )}
                </div>

              </div>
            </div>
          </div>

          {/* CENTER — Line Items */}
          <div className="ims-panel-center">
            <div className="ims-card ims-lines-card">
              <div className="ims-lines-header">
                <h2 className="ims-card-title">{t('purchaseOrder.lineItems')}</h2>
                {loadingItems && <span>{t('common.loading')}</span>}
                <button
                  type="button"
                  className="ims-btn ims-btn-secondary"
                  onClick={addLine}
                >
                  <Plus size={13} strokeWidth={2} />
                  {t('purchaseOrder.addLineItem')}
                </button>
              </div>

              {formErrors.lines && (
                <div style={{ padding: '6px 16px 0' }}>
                  <span className="ims-field-error">{formErrors.lines}</span>
                </div>
              )}

              <div className="ims-lines-body">
                <div className="ims-table-scroll">
                  <table className="ims-table">
                    <thead>
                      <tr>
                        <th className="ims-th">{t('purchaseOrder.product')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseOrder.quantity')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseOrder.unitPrice')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseOrder.taxRate', 'Tax %')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseOrder.taxAmount', 'Tax Amt')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseOrder.lineTotal')}</th>
                        <th className="ims-th ims-th--del" />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, index) => {
                        const tax = lineTaxAmt(line);
                        const total = lineTotal(line);
                        return (
                          <tr key={index} className="ims-tr">
                            <td className="ims-td">
                              {purchaseItems.length > 0 ? (
                                <select
                                  className="ims-cell-input"
                                  value={line.purchaseItemId ?? ''}
                                  onChange={(e) => handleLineItemSelect(index, e.target.value)}
                                >
                                  <option value="">{t('purchaseOrder.selectProduct')}</option>
                                  {purchaseItems.map((p) => (
                                    <option key={p.purchaseItemId} value={p.purchaseItemId}>
                                      {p.productName} ({p.productSKU})
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  className="ims-cell-input"
                                  placeholder={
                                    formData.supplierId
                                      ? t('purchaseOrder.noItemsForSupplier')
                                      : t('purchaseOrder.selectSupplierFirst')
                                  }
                                  value={line.productName}
                                  onChange={(e) =>
                                    setLines((prev) => {
                                      const updated = [...prev];
                                      updated[index] = { ...updated[index], productName: e.target.value };
                                      return updated;
                                    })
                                  }
                                  disabled={!formData.supplierId || loadingItems}
                                />
                              )}
                            </td>
                            <td className="ims-td ims-td--num">
                              <input
                                type="number"
                                className={`ims-cell-input ims-num-input ${lineErrors[index]?.quantity ? 'is-invalid' : ''}`}
                                value={line.quantity}
                                onChange={(e) => handleLineChange(index, 'quantity', e.target.value)}
                              />
                              {lineErrors[index]?.quantity && (
                                <div className="ims-field-error">{lineErrors[index]?.quantity}</div>
                              )}
                            </td>
                            <td className="ims-td ims-td--num">
                              <input
                                type="number"
                                className={`ims-cell-input ims-num-input ${lineErrors[index]?.unitPrice ? 'is-invalid' : ''}`}
                                step={0.01}
                                value={line.unitPrice}
                                onChange={(e) => handleLineChange(index, 'unitPrice', e.target.value)}
                              />
                              {lineErrors[index]?.unitPrice && (
                                <div className="ims-field-error">{lineErrors[index]?.unitPrice}</div>
                              )}
                            </td>
                            <td className="ims-td ims-td--num po-tax-cell">
                              {line.taxRate > 0 ? `${line.taxRate}%` : '—'}
                            </td>
                            <td className="ims-td ims-td--num po-tax-cell">
                              {tax > 0 ? fmt(tax) : '—'}
                            </td>
                            <td className="ims-td ims-td--num">
                              {fmt(total)}
                            </td>
                            <td className="ims-td ims-td--del">
                              <button
                                type="button"
                                className="ims-remove-btn"
                                onClick={() => removeLine(index)}
                                disabled={lines.length === 1}
                                title={t('purchaseOrder.removeLineItem')}
                              >
                                <X size={14} strokeWidth={2} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} className="ims-td ims-td--num po-tfoot-label">{t('purchaseOrder.subtotal', 'Subtotal')}</td>
                        <td className="ims-td ims-td--num po-tfoot-value">{fmt(subtotal)}</td>
                        <td className="ims-td ims-td--del" />
                      </tr>
                      <tr>
                        <td colSpan={5} className="ims-td ims-td--num po-tfoot-label">{t('purchaseOrder.totalTax', 'Total Tax')}</td>
                        <td className="ims-td ims-td--num po-tfoot-value">{fmt(totalTax)}</td>
                        <td className="ims-td ims-td--del" />
                      </tr>
                      <tr className="po-tfoot-grand">
                        <td colSpan={5} className="ims-td ims-td--num po-tfoot-label">{t('purchaseOrder.total', 'Total')}</td>
                        <td className="ims-td ims-td--num po-tfoot-total">{fmt(grandTotal)}</td>
                        <td className="ims-td ims-td--del" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Summary + Help */}
          <div className="ims-panel-right">
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('purchaseOrder.orderSummary')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('purchaseOrder.itemCount')}</span>
                  <span className="ims-summary-value">{validItemCount}</span>
                </div>
                <hr style={{ margin: '8px 0', borderColor: '#f3f4f6' }} />
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('purchaseOrder.subtotal', 'Subtotal')}</span>
                  <span className="ims-summary-value">{fmt(subtotal)}</span>
                </div>
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('purchaseOrder.totalTax', 'Total Tax')}</span>
                  <span className="ims-summary-value">{fmt(totalTax)}</span>
                </div>
                <hr style={{ margin: '8px 0', borderColor: '#f3f4f6' }} />
                <div className="ims-summary-row ims-summary-total">
                  <span>{t('purchaseOrder.total', 'Total')}</span>
                  <span>{fmt(grandTotal)}</span>
                </div>
              </div>
            </div>

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('purchaseOrder.requiredFields')}</h3>
              </div>
              <div className="ims-card-body">
                <ul className="ims-help-list">
                  <li>{t('purchaseOrder.supplier')}</li>
                  <li>{t('purchaseOrder.orderDate')}</li>
                  <li>{t('purchaseOrder.expectedDelivery')}</li>
                  <li>{t('purchaseOrder.lineItems')}</li>
                </ul>
                <hr style={{ margin: '8px 0', borderColor: '#f3f4f6' }} />
                <p style={{ fontSize: 11, fontWeight: 600, color: '#374151', margin: '8px 0 4px', textTransform: 'uppercase', letterSpacing: '.4px' }}>{t('purchaseOrder.optionalFields')}</p>
                <ul className="ims-help-list">
                  <li>{t('purchaseOrder.notes')}</li>
                </ul>
              </div>
            </div>
          </div>

        </div>
      </form>
    </Layout>
  );
};

export default PurchaseOrderCreatePage;
